import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {api} from 'utils/api';
import {omit} from 'lodash';
import invariant from 'invariant';

export const defaultQueryIdIndex = 'index';
export const defaultQueryIdById = 'byId';

export const defaultUnwrap = ({data}) => data;

export const defaultUseQueryOptionsBase = {
	refetchOnWindowFocus: false,
	staleTime: 60 * 1000,
};

/**
 * Analyze query error to determine error type
 * @param {Object} error - The error object from react-query
 * @returns {Object} - { hasPermissionError, hasNetworkError, statusCode }
 */
export const analyzeQueryError = error => {
	if (!error) {
		return {
			hasPermissionError: false,
			hasNetworkError: false,
			statusCode: null,
		};
	}

	// Handle both axios errors (error.response.status) and fetch Response errors (error.status)
	// Also handle cases where status might be a string or have additional properties
	let statusCode = error?.response?.status || error?.status;

	const result = {
		hasPermissionError: statusCode === 403,
		hasNetworkError: !statusCode || statusCode >= 500,
		statusCode,
	};

	return result;
};

export const resolveApiPath =
	(pathName = '') =>
	(...pathArgs) => {
		if (!pathArgs.length) return pathName;

		const parts = pathArgs.slice(0, -1);
		const last = pathArgs[pathArgs.length - 1];

		const basePath = parts.reduce((acc, path) => `${acc}/${path}`, pathName);

		if (!last) {
			return basePath;
		}

		if (typeof last === 'object') {
			const params = new URLSearchParams(last).toString();
			return params ? `${basePath}?${params}` : basePath;
		}

		if (last.startsWith && last.startsWith('?')) {
			return `${basePath}${last}`;
		}

		return `${basePath}/${last}`;
	};

export const createApiResource = ({
	name,
	pathName,
	defaultIncludes = [],
	queryIdIndex = defaultQueryIdIndex,
	queryIdById = defaultQueryIdById,
	unwrap = defaultUnwrap,
	enableSchema = false,
	cascadeTo = [],
}) => {
	const getQueryKey = (...args) => [name, ...args];
	const getResourcePath = resolveApiPath(pathName);
	const getSchemaPath = () => `${pathName}/schema`;
	const mergeDefaultParams = (params = {}) => {
		const includes = [...new Set([...defaultIncludes, ...(params.include || [])])].sort();
		return {
			...params,
			...(includes.length ? {include: includes.join(',')} : {}),
		};
	};

	const createQueryClientHelpers = () => {
		const invalidateIndex = (queryClient, params = null) => {
			const hasParams =
				params && typeof params === 'object' && Object.keys(params).length > 0;
			// Invalidate all index variants when no params are provided.
			queryClient.invalidateQueries({
				queryKey: hasParams
					? getQueryKey(queryIdIndex, params)
					: getQueryKey(queryIdIndex),
			});
		};

		const invalidateById = (queryClient, id, params = {}) => {
			queryClient.invalidateQueries({queryKey: getQueryKey(queryIdById, id, params)});
		};

		const invalidateAllResourceQueries = queryClient => {
			queryClient.invalidateQueries({queryKey: getQueryKey()});
		};

		const invalidateCustom = (queryClient, queryId, params = {}) => {
			const queryKey =
				Object.keys(params).length === 0
					? getQueryKey(queryId)
					: getQueryKey(queryId, params);
			queryClient.invalidateQueries({queryKey});
		};

		const cancelAllResourceQueries = queryClient => {
			queryClient.cancelQueries({queryKey: getQueryKey()});
		};

		const removeById = (queryClient, id) => {
			queryClient.removeQueries({queryKey: getQueryKey(queryIdById, id)});
		};

		const removeAllResourceQueries = queryClient => {
			queryClient.removeQueries({queryKey: getQueryKey()});
		};

		const setByIdData = (queryClient, id, params, data) => {
			queryClient.setQueryData(getQueryKey(queryIdById, id, params), data);
		};

		const setCustomData = (queryClient, queryId, params, data) => {
			const queryKey =
				Object.keys(params).length === 0
					? getQueryKey(queryId)
					: getQueryKey(queryId, params);
			queryClient.setQueryData(queryKey, data);
		};

		return {
			invalidateIndex,
			invalidateById,
			invalidateCustom,
			invalidateAllResourceQueries,
			cancelAllResourceQueries,
			removeById,
			removeAllResourceQueries,
			setByIdData,
			setCustomData,
		};
	};

	const createUseQueryIndex = ({
		queryId = queryIdIndex,
		defaultParams = {},
		defaultUseQueryOptions = {},
	} = {}) => {
		const useQueryIndex = function ({params = {}, useQueryOptions = {}} = {}) {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};
			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				queryFn: () => api.get(getResourcePath(), {params: ps}).then(unwrap),
				queryKey: getQueryKey(queryId, ps),
			});
		};

		// Expose query configuration for direct usage (e.g., in validators)
		useQueryIndex.getQueryKey = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return getQueryKey(queryId, ps);
		};
		useQueryIndex.getQueryFn = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return () => api.get(getResourcePath(), {params: ps}).then(unwrap);
		};

		return useQueryIndex;
	};

	const createUseQueryIndexPost = ({
		queryId = queryIdIndex,
		defaultBody = {},
		defaultUseQueryOptions = {},
		pathSuffix = 'query',
	} = {}) => {
		const useQueryIndexPost = function ({params = {}, useQueryOptions = {}} = {}) {
			// Keep the external callsite contract consistent: callers pass `{params: <body>}`.
			const body = {...defaultBody, ...params};
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};

			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				queryFn: () => api.post(getResourcePath(pathSuffix), {body}).then(unwrap),
				queryKey: getQueryKey(queryId, body),
			});
		};

		// Expose query configuration for direct usage (e.g., in validators)
		useQueryIndexPost.getQueryKey = params => {
			const body = {...defaultBody, ...params};
			return getQueryKey(queryId, body);
		};
		useQueryIndexPost.getQueryFn = params => {
			const body = {...defaultBody, ...params};
			return () => api.post(getResourcePath(pathSuffix), {body}).then(unwrap);
		};

		return useQueryIndexPost;
	};

	const createUseQueryById = ({
		queryId = queryIdById,
		defaultParams = {},
		defaultUseQueryOptions = {},
	} = {}) => {
		const useQueryById = function ({id, params = {}, useQueryOptions = {}}) {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};
			const enabled =
				!!id &&
				(Object.prototype.hasOwnProperty.call(options, 'enabled')
					? !!options.enabled
					: true);
			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				enabled,
				queryKey: getQueryKey(queryId, id, ps),
				queryFn: () => api.get(getResourcePath(id), {params: ps}).then(unwrap),
			});
		};

		// Expose query configuration for direct usage
		useQueryById.getQueryKey = (id, params) => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return getQueryKey(queryId, id, ps);
		};
		useQueryById.getQueryFn = (id, params) => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return () => api.get(getResourcePath(id), {params: ps}).then(unwrap);
		};

		return useQueryById;
	};

	const createUseCustomQuery = ({
		queryId,
		pathName,
		defaultParams = {},
		defaultUseQueryOptions = {},
	} = {}) => {
		invariant(queryId, 'queryId is required');
		invariant(pathName, 'pathName is required');

		const useCustomQuery = function ({params = {}, useQueryOptions = {}} = {}) {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};
			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				queryKey: getQueryKey(queryId, ps),
				queryFn: () => api.get(getResourcePath(pathName), {params: ps}).then(unwrap),
			});
		};

		// Expose query configuration for direct usage
		useCustomQuery.getQueryKey = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return getQueryKey(queryId, ps);
		};
		useCustomQuery.getQueryFn = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return () => api.get(getResourcePath(pathName), {params: ps}).then(unwrap);
		};

		return useCustomQuery;
	};

	const createUseQuerySchema = ({defaultUseQueryOptions = {}} = {}) => {
		invariant(enableSchema, 'enableSchema must be true to use schema queries');

		const useQuerySchema = function ({
			params = {},
			partition = {},
			useQueryOptions = {},
		} = {}) {
			const hasParams =
				params && typeof params === 'object' && Object.keys(params).length > 0;
			const hasPartition =
				partition && typeof partition === 'object' && Object.keys(partition).length > 0;
			const schemaParams = hasParams ? params : {};
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
				// Global schema is static; runtime schema callers can override these options.
				staleTime: 5 * 60 * 1000, // 5 minutes
				cacheTime: 10 * 60 * 1000, // 10 minutes
			};

			const queryKey = hasPartition
				? getQueryKey('schema', schemaParams, {partition})
				: hasParams
				? getQueryKey('schema', schemaParams)
				: getQueryKey('schema');

			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				queryKey,
				queryFn: () => api.get(getSchemaPath(), {params: schemaParams}).then(unwrap),
			});
		};

		// Expose query configuration for direct usage
		useQuerySchema.getQueryKey = (params = {}, partition = {}) => {
			const hasParams =
				params && typeof params === 'object' && Object.keys(params).length > 0;
			const hasPartition =
				partition && typeof partition === 'object' && Object.keys(partition).length > 0;
			const schemaParams = hasParams ? params : {};
			if (hasPartition) return getQueryKey('schema', schemaParams, {partition});
			return hasParams ? getQueryKey('schema', schemaParams) : getQueryKey('schema');
		};
		useQuerySchema.getQueryFn = (params = {}) => {
			const hasParams =
				params && typeof params === 'object' && Object.keys(params).length > 0;
			const schemaParams = hasParams ? params : {};
			return () => api.get(getSchemaPath(), {params: schemaParams}).then(unwrap);
		};

		return useQuerySchema;
	};

	const {invalidateIndex, invalidateById, removeById, cancelAllResourceQueries} =
		createQueryClientHelpers();

	// cascadeTo: cancel and invalidate dependent resources alongside this resource's own queries.
	const cancelCascade = queryClient => {
		cascadeTo.forEach(prefix => queryClient.cancelQueries({queryKey: [prefix]}));
	};
	const invalidateCascade = queryClient => {
		cascadeTo.forEach(prefix => queryClient.invalidateQueries({queryKey: [prefix]}));
	};

	const createUseMutationCreate = ({
		defaultUseMutationOptions = {},
		onMutate = () => {},
		onSettled = () => {},
		onError = () => {},
	} = {}) =>
		function useMutationCreate({useMutationOptions = {}} = {}) {
			const queryClient = useQueryClient();
			const options = {
				...defaultUseMutationOptions,
				...useMutationOptions,
			};
			return useMutation({
				...omit(options, ['mutationFn', 'onMutate', 'onSettled', 'onError', 'select']),
				mutationFn: x => api.post(getResourcePath(), {body: x}).then(unwrap),
				onMutate: x => {
					cancelAllResourceQueries(queryClient);
					cancelCascade(queryClient);
					return onMutate(x, {queryClient});
				},
				onSettled: (_, __, x, ctx) => {
					invalidateIndex(queryClient);
					invalidateCascade(queryClient);
					onSettled(_, __, x, {queryClient, ...ctx});
				},
				onError: (err, x, ctx) => {
					onError(err, x, {queryClient, ...ctx});
				},
			});
		};

	const createUseMutationUpdate = ({
		mode = 'put',
		defaultUseMutationOptions = {},
		onMutate = () => {},
		onSettled = () => {},
		onError = () => {},
	} = {}) =>
		function useMutationUpdate({useMutationOptions = {}} = {}) {
			invariant(
				mode === 'put' || mode === 'patch',
				`createUseMutationUpdate: unsupported mode "${mode}"`,
			);

			const queryClient = useQueryClient();
			const options = {
				...defaultUseMutationOptions,
				...useMutationOptions,
			};
			const request = mode === 'patch' ? api.patch : api.put;
			return useMutation({
				...omit(options, ['mutationFn', 'onMutate', 'onSettled', 'onError', 'select']),
				mutationFn: x => {
					invariant(x && typeof x === 'object', 'Update payload must be an object');
					invariant(
						x.id !== undefined && x.id !== null,
						'Update payload must include id',
					);
					const {id, ...body} = x;
					return request(getResourcePath(id), {body}).then(unwrap);
				},
				onMutate: x => {
					cancelAllResourceQueries(queryClient);
					cancelCascade(queryClient);
					return onMutate(x, {queryClient});
				},
				onSettled: (_, __, x, ctx) => {
					invalidateIndex(queryClient);
					invalidateById(queryClient, x.id);
					invalidateCascade(queryClient);
					onSettled(_, __, x, {queryClient, ...ctx});
				},
				onError: (err, x, ctx) => {
					onError(err, x, {queryClient, ...ctx});
				},
			});
		};

	const createUseMutationDelete = ({
		defaultUseMutationOptions = {},
		onMutate = () => {},
		onSettled = () => {},
		onError = () => {},
	} = {}) =>
		function useMutationDelete({useMutationOptions = {}} = {}) {
			const queryClient = useQueryClient();
			const options = {
				...defaultUseMutationOptions,
				...useMutationOptions,
			};
			return useMutation({
				...omit(options, ['mutationFn', 'onMutate', 'onSettled', 'onError', 'select']),
				mutationFn: x => {
					return api.remove(getResourcePath(x.id)).then(response => {
						// Handle null/empty responses from DELETE (204 No Content or empty body)
						if (response === null || response === undefined) {
							return null;
						}
						// If response has data property, unwrap it
						if (response.data !== undefined) {
							return unwrap(response);
						}
						// Otherwise return response as-is (already unwrapped or plain object)
						return response;
					});
				},
				onMutate: x => {
					cancelAllResourceQueries(queryClient);
					cancelCascade(queryClient);
					return onMutate(x, {queryClient});
				},
				onSettled: (_, __, x, ctx) => {
					// Remove the deleted item from cache instead of invalidating (which would try to refetch)
					if (x?.id) {
						removeById(queryClient, x.id);
					}
					// Invalidate index queries to refresh the list
					invalidateIndex(queryClient);
					invalidateCascade(queryClient);
					onSettled(_, __, x, {queryClient, ...ctx});
				},
				onError: (err, x, ctx) => {
					// Remove the deleted item from cache even on error (if deletion partially succeeded)
					if (x?.id) {
						removeById(queryClient, x.id);
					}
					// Still invalidate index queries
					invalidateIndex(queryClient);
					invalidateCascade(queryClient);
					onError(err, x, {queryClient, ...ctx});
				},
			});
		};

	return {
		getQueryKey,
		getResourcePath,
		getSchemaPath,
		mergeDefaultParams,
		createUseQueryIndex,
		createUseQueryIndexPost,
		createUseQueryById,
		createUseCustomQuery,
		createUseQuerySchema: enableSchema ? createUseQuerySchema : undefined,
		createUseMutationCreate,
		createUseMutationUpdate,
		createUseMutationDelete,
		createQueryClientHelpers,
	};
};
